import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { hasAuthParams } from 'react-oidc-context'
import { ROUTES } from 'app/constants/routes'

/** Parametry, które Keycloak dokleja do redirect_uri przy powrocie. */
const AUTH_CALLBACK_PARAMS = [
  'code',
  'state',
  'session_state',
  'error',
  'error_description',
  'iss',
]

/** Zdejmuje z bieżącego adresu parametry powrotu z Keycloaka (bez przeładowania). */
const stripAuthParamsFromUrl = () => {
  const url = new URL(window.location.href)
  AUTH_CALLBACK_PARAMS.forEach((p) => url.searchParams.delete(p))
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash)
}

/**
 * Bieżący adres w formie przekazywanej jako `url_state` przy signinRedirect —
 * po zalogowaniu /postLogin wraca na ten adres (deep link).
 */
export const currentUrlState = () =>
  window.location.pathname + window.location.search

/**
 * Techniczne trasy flow logowania — /postLogin nigdy nie wraca na nie po
 * zalogowaniu (powrót na nie zapętlałby logowanie); ląduje się na domyślnym
 * dashboardzie.
 */
export const NON_RESTORABLE_ROUTES = [
  ROUTES.PostLogin,
  ROUTES.LoggedOut,
  ROUTES.LogoutComplete,
]

export const userManager = new UserManager({
  authority: process.env.KEYCLOAK_URL + '/realms/' + process.env.KEYCLOAK_REALM,
  client_id: process.env.KEYCLOAK_CLIENT_ID,
  // Bez client_secret — wszystkie klienty Keycloak muszą być PUBLIC z PKCE
  // (S256). Confidential client odrzuci wymianę kodu 401-ką "Invalid client
  // or Invalid client credentials".
  redirect_uri: process.env.KEYCLOAK_REDIRECT_URI,
  // Request profile + email scopes so JWT carries preferred_username, given_name,
  // family_name and email. The backend's JIT user provisioning
  // (HttpContextViewerFactory.TryProvisionUserFromClaimsAsync) needs them.
  scope: 'openid profile email',
  // Jedyny URI przepuszczany przez whitelist "Valid post logout redirect URIs"
  // klienta supertacho w Keycloaku (root i /postLogin dostają 400). Trasa
  // /logged-out w AppRoutes nie renderuje żadnej strony "wylogowano" — od razu
  // przekierowuje na formularz logowania Keycloak. Gdyby kiedyś poszerzyć
  // whitelist o root, można ten hop usunąć.
  post_logout_redirect_uri: new URL(ROUTES.LoggedOut, window.location.origin).toString(),
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  // Po jakim czasie wpis stanu OIDC (zapisywany przed wysłaniem na Keycloaka)
  // uchodzi za porzucony i leci przy najbliższym signinRedirect. Domyślne
  // 15 min bywa krótsze niż realne logowanie (formularz otwarty w tle, hasło
  // z menedżera, 2FA, uśpiony laptop) — skasowany wpis to przy powrocie twarde
  // "No matching state found in storage". Keycloak i tak ubija sesję logowania
  // po 30 min, więc godzina jest bezpiecznym marginesem.
  //
  // NIE dokładamy tu własnego userManager.clearStaleState() na starcie modułu:
  // oidc-client-ts robi to sam w createSigninRequest, tuż przed zapisem nowego
  // stanu. Wywołane z poziomu modułu leciałoby też na stronie powrotu
  // z Keycloaka i asynchronicznie ścigało się z wymianą ?code= na token o ten
  // sam wpis — przegrany wyścig to dokładnie ten błąd.
  staleStateAgeInSeconds: 60 * 60,
  // Celowo false: check-session iframe wymaga third-party cookies do domeny
  // Keycloaka, które współczesne przeglądarki blokują — mechanizm jest
  // niewiarygodny. Wylogowanie w innej karcie i tak zostaje wykryte przy
  // najbliższym odświeżeniu tokenu (refresh token unieważniony → silent renew
  // error → redirectToLogin → formularz logowania).
  monitorSession: false,
})

let isRedirecting = false

/**
 * Jedyne miejsce, z którego wolno wywołać pełny redirect na logowanie Keycloak
 * poza interakcją użytkownika. Guardy:
 * - `hasAuthParams()` — jesteśmy w trakcie obsługi powrotu z Keycloaka
 *   (?code=&state= w URL). Redirect w tym momencie przerywa wymianę kodu na
 *   token i powoduje pętlę przeładowań (nowy code przy każdym obrocie).
 * - `isRedirecting` — deduplikacja w obrębie jednego załadowania strony.
 * Bieżący adres jedzie w `url_state` (natywny mechanizm oidc-client-ts,
 * doklejany do parametru OAuth `state`), żeby /postLogin mógł na niego wrócić.
 */
export const redirectToLogin = () => {
  if (isRedirecting || hasAuthParams()) return
  isRedirecting = true
  userManager
    .signinRedirect({ url_state: currentUrlState() })
    .catch((err) => {
      console.error('signinRedirect failed:', err)
      isRedirecting = false
    })
}

// Znacznik "ta karta już raz posprzątała adres po nieudanym callbacku".
// Kasowany po udanym zalogowaniu.
const CALLBACK_FAILED_KEY = 'et.auth.callback-failed'

const readFlag = (key: string) => {
  try {
    return window.sessionStorage.getItem(key) !== null
  } catch {
    return false
  }
}

const writeFlag = (key: string, value: boolean) => {
  try {
    if (value) window.sessionStorage.setItem(key, '1')
    else window.sessionStorage.removeItem(key)
  } catch {
    /* sessionStorage niedostępne (tryb prywatny) — guard po prostu odpada */
  }
}

/**
 * Nieudany powrót z Keycloaka ("No matching state found in storage" po
 * przywróceniu karty / cofnięciu przeglądarką, zużyty ?code=). Biblioteka
 * czyści adres tylko po sukcesie (onSigninCallback), więc martwe ?code=&state=
 * zostają — i każde odświeżenie odtwarza dokładnie ten sam błąd. Zdejmujemy je,
 * dzięki czemu odświeżenie startuje czyste logowanie, a udokumentowany
 * auto-signin z ProtectedApp (warunkowany `hasAuthParams()`) może od razu
 * spróbować jeszcze raz.
 *
 * Tylko RAZ na kartę — przy powtórce zostawiamy parametry w adresie, co przez
 * ten sam `hasAuthParams()` blokuje auto-signin i zatrzymuje nas na ekranie
 * błędu zamiast w pętli redirectów.
 */
export const clearFailedCallbackFromUrl = () => {
  if (!hasAuthParams() || readFlag(CALLBACK_FAILED_KEY)) return
  writeFlag(CALLBACK_FAILED_KEY, true)
  stripAuthParamsFromUrl()
}

/**
 * Jesteśmy zalogowani — zdejmij blokadę, żeby kolejna awaria callbacku znów
 * dostała swoje jedno posprzątanie adresu. Nie wystarczy zrobić tego
 * w onSigninCallback: po posprzątanym adresie sesja wraca ze storage'u
 * (getUser), bez żadnego callbacku.
 */
export const resetFailedCallbackGuard = () => writeFlag(CALLBACK_FAILED_KEY, false)

userManager.events.addSilentRenewError((error) => {
  console.error('Silent token renewal failed:', error)
  redirectToLogin()
})

userManager.events.addAccessTokenExpired(() => {
  // Token wygasa "na żywo" głównie po uśpieniu komputera / uśpieniu karty,
  // kiedy timery silent renew nie zdążyły się wykonać. Sesja SSO zwykle nadal
  // żyje, więc najpierw próbujemy cichego odświeżenia (bez przeładowania
  // strony); pełny redirect dopiero, gdy refresh token też jest martwy.
  console.warn('Access token expired, attempting silent renew')
  userManager.signinSilent().catch(() => redirectToLogin())
})

export const onSigninCallback = () => {
  stripAuthParamsFromUrl()
}
